import React, { createContext, useContext, useState, useEffect, useCallback, useRef, ReactNode } from 'react';
import { OrderOut } from '../types/api';
import { apiService } from '../services/api';
import { useAuth } from './AuthContext';

interface OrderHistoryContextType {
  orders: OrderOut[];
  addOrder: (order: OrderOut) => void;
  getOrder: (orderId: number) => OrderOut | undefined;
  updateOrderFromDatabase: (orderId: number) => Promise<void>;
  syncOrdersFromDatabase: (force?: boolean) => Promise<void>;
  clearOrderHistory: () => void;
  hasOrders: boolean;
  isSyncing: boolean;
}

const OrderHistoryContext = createContext<OrderHistoryContextType | undefined>(undefined);

interface OrderHistoryProviderProps {
  children: ReactNode;
}

const SESSION_STORAGE_KEY = 'priorpay_order_history';
const ANONYMOUS_SESSION_KEY = 'priorpay_anonymous_session_id';
const ANONYMOUS_QR_CODES_KEY = 'priorpay_anonymous_qr_codes'; // Store QR codes for anonymous orders

// Generate or retrieve anonymous session ID
const getAnonymousSessionId = (): string => {
  let sessionId = sessionStorage.getItem(ANONYMOUS_SESSION_KEY);
  if (!sessionId) {
    sessionId = `anon_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
    sessionStorage.setItem(ANONYMOUS_SESSION_KEY, sessionId);
  }
  return sessionId;
};

export const OrderHistoryProvider: React.FC<OrderHistoryProviderProps> = ({ children }) => {
  const [orders, setOrders] = useState<OrderOut[]>([]);
  const { isAuthenticated, user } = useAuth();
  const [lastSyncTime, setLastSyncTime] = useState<number>(0);
  const [isSyncing, setIsSyncing] = useState(false);
  const [orderUpdateCache, setOrderUpdateCache] = useState<Map<number, number>>(new Map());
  const isLoadingOrdersRef = useRef(false);
  const loadingQrCodesRef = useRef<Set<string>>(new Set());

  // Helper function to filter orders by ownership (memoized to avoid dependency issues)
  const filterOrdersByOwnership = useCallback((ordersToFilter: OrderOut[]): OrderOut[] => {
    if (isAuthenticated && user) {
      // For authenticated users: only show orders where customer_id matches user.id
      return ordersToFilter.filter(order => order.customer_id === user.id);
    } else {
      // For anonymous users: clear ALL orders - they should only see orders added in this session
      // Anonymous users should not see any orders from sessionStorage that might belong to other users
      // They will only see orders that are explicitly added via addOrder() during this session
      return [];
    }
  }, [isAuthenticated, user]);

  // Load orders from session storage on mount and filter by ownership
  useEffect(() => {
    // Prevent duplicate concurrent calls (e.g., from React StrictMode)
    if (isLoadingOrdersRef.current) {
      return;
    }
    isLoadingOrdersRef.current = true;

    const loadOrdersFromStorage = async () => {
      try {
        // If user is authenticated, load orders from sessionStorage
        if (isAuthenticated && user) {
          const storedOrders = sessionStorage.getItem(SESSION_STORAGE_KEY);
          if (storedOrders) {
            const parsedOrders: OrderOut[] = JSON.parse(storedOrders);
            console.log('Loaded orders from session storage:', parsedOrders);
            // Filter orders by ownership before setting state
            const filteredOrders = filterOrdersByOwnership(parsedOrders);
            console.log('Filtered orders by ownership:', filteredOrders);
            setOrders(filteredOrders);
            
            // If filtering removed orders, update sessionStorage
            if (filteredOrders.length !== parsedOrders.length) {
              if (filteredOrders.length > 0) {
                sessionStorage.setItem(SESSION_STORAGE_KEY, JSON.stringify(filteredOrders));
              } else {
                sessionStorage.removeItem(SESSION_STORAGE_KEY);
              }
            }
          }
          isLoadingOrdersRef.current = false;
        } else {
          // For anonymous users: Load orders using QR codes from localStorage
          console.log('Anonymous user detected - loading orders by QR code');
          const loadAnonymousOrders = async () => {
            try {
              const storedQrCodes = localStorage.getItem(ANONYMOUS_QR_CODES_KEY);
              if (!storedQrCodes) {
                console.log('No QR codes found in localStorage for anonymous user');
                setOrders([]);
                isLoadingOrdersRef.current = false;
                return;
              }

              const qrCodes: string[] = JSON.parse(storedQrCodes);
              console.log('Found QR codes in localStorage:', qrCodes);

              // Filter out QR codes that are already being loaded to prevent duplicates
              const qrCodesToLoad = qrCodes.filter(qrCode => {
                if (loadingQrCodesRef.current.has(qrCode)) {
                  console.log(`QR code ${qrCode} is already being loaded, skipping duplicate request`);
                  return false;
                }
                loadingQrCodesRef.current.add(qrCode);
                return true;
              });

              // Fetch orders using QR codes
              const orderPromises = qrCodesToLoad.map(qrCode => 
                apiService.getAnonymousOrderByQr(qrCode)
                  .catch(error => {
                    const status = error?.response?.status;
                    if (status == 400) {
                      try {
                        const currentStored = localStorage.getItem(ANONYMOUS_QR_CODES_KEY);
                        const currentCodes: string[] = currentStored ? JSON.parse(currentStored) : [];
                        const updatedQrCodes = currentCodes.filter(code => code !== qrCode);
                        
                        if (updatedQrCodes.length > 0) {
                          localStorage.setItem(ANONYMOUS_QR_CODES_KEY, JSON.stringify(updatedQrCodes));
                        } else {
                          localStorage.removeItem(ANONYMOUS_QR_CODES_KEY);
                        }
                      } catch (storageError) {
                        console.error('Error removing unusable QR code from localStorage:', storageError);
                      }
                    }
                    console.warn(`Failed to fetch order with QR code ${qrCode}:`, error);
                    return null;
                  })
                  .finally(() => {
                    // Remove from loading set when done (success or error)
                    loadingQrCodesRef.current.delete(qrCode);
                  })
              );

              const fetchedOrders = await Promise.all(orderPromises);
              const validOrders = fetchedOrders.filter((order): order is OrderOut => order !== null);
              
              console.log('Loaded anonymous orders by QR code:', validOrders);
              setOrders(validOrders);
            } catch (error) {
              console.error('Error loading anonymous orders:', error);
              setOrders([]);
            } finally {
              isLoadingOrdersRef.current = false;
            }
          };

          loadAnonymousOrders();
        }
      } catch (error) {
        console.error('Error loading orders from session storage:', error);
        // Clear corrupted data
        sessionStorage.removeItem(SESSION_STORAGE_KEY);
        setOrders([]);
      } finally {
        isLoadingOrdersRef.current = false;
      }
    };

    loadOrdersFromStorage();
  }, [isAuthenticated, user, filterOrdersByOwnership]);

  // Filter orders whenever authentication status or user changes
  useEffect(() => {
    setOrders(prevOrders => {
      const filtered = filterOrdersByOwnership(prevOrders);
      if (filtered.length !== prevOrders.length) {
        console.log('Filtered orders due to auth/user change:', {
          before: prevOrders.length,
          after: filtered.length
        });
      }
      return filtered;
    });
  }, [isAuthenticated, user, filterOrdersByOwnership]);

  // Save orders to session storage whenever orders change (only for authenticated users)
  useEffect(() => {
    // Only save orders for authenticated users
    if (!isAuthenticated || !user) {
      // Don't save orders for anonymous users
      return;
    }

    try {
      // Only save filtered orders
      const filteredOrders = filterOrdersByOwnership(orders);
      if (filteredOrders.length > 0) {
        console.log('Saving orders to session storage:', filteredOrders);
        sessionStorage.setItem(SESSION_STORAGE_KEY, JSON.stringify(filteredOrders));
      } else {
        // Remove from storage if no orders
        sessionStorage.removeItem(SESSION_STORAGE_KEY);
      }
    } catch (error) {
      console.error('Error saving orders to session storage:', error);
    }
  }, [orders, isAuthenticated, user, filterOrdersByOwnership]);

  const addOrder = (order: OrderOut) => {
    console.log('Adding order to history:', order);
    
    // Verify ownership before adding
    if (isAuthenticated && user) {
      if (order.customer_id !== user.id) {
        console.warn('Attempted to add order that does not belong to current user:', {
          orderId: order.id,
          orderCustomerId: order.customer_id,
          currentUserId: user.id
        });
        return; // Don't add orders that don't belong to the current user
      }
    } else {
      // For anonymous users: store QR code in localStorage for later retrieval
      if (order.qr_code_short || order.qr_code) {
        try {
          const storedQrCodes = localStorage.getItem(ANONYMOUS_QR_CODES_KEY);
          const qrCodes: string[] = storedQrCodes ? JSON.parse(storedQrCodes) : [];
          const qrCodeToStore = order.qr_code_short || order.qr_code;
          
          // Only add if not already stored
          if (qrCodeToStore && !qrCodes.includes(qrCodeToStore)) {
            qrCodes.push(qrCodeToStore);
            localStorage.setItem(ANONYMOUS_QR_CODES_KEY, JSON.stringify(qrCodes));
            console.log('Stored QR code for anonymous order:', qrCodeToStore);
          }
        } catch (error) {
          console.error('Error storing QR code:', error);
        }
      }
    }
    
    setOrders(prevOrders => {
      // Check if order already exists (by ID)
      const existingOrderIndex = prevOrders.findIndex(o => o.id === order.id);
      if (existingOrderIndex >= 0) {
        // Update existing order
        const updatedOrders = [...prevOrders];
        updatedOrders[existingOrderIndex] = order;
        // For anonymous users, don't filter (they can see their own orders)
        // For authenticated users, filter to ensure only their orders
        return isAuthenticated && user 
          ? filterOrdersByOwnership(updatedOrders)
          : updatedOrders;
      } else {
        // Add new order at the beginning (most recent first)
        // For anonymous users, don't filter (they can see their own orders)
        // For authenticated users, filter to ensure only their orders
        return isAuthenticated && user
          ? filterOrdersByOwnership([order, ...prevOrders])
          : [order, ...prevOrders];
      }
    });
  };

  const getOrder = (orderId: number): OrderOut | undefined => {
    const order = orders.find(order => order.id === orderId);
    // Verify ownership before returning
    if (order && isAuthenticated && user) {
      if (order.customer_id !== user.id) {
        console.warn('Attempted to get order that does not belong to current user:', {
          orderId,
          orderCustomerId: order.customer_id,
          currentUserId: user.id
        });
        return undefined;
      }
    }
    return order;
  };

  const updateOrderFromDatabase = async (orderId: number): Promise<void> => {
    // Check if we recently updated this order (cache for 10 seconds)
    const now = Date.now();
    const ORDER_UPDATE_COOLDOWN = 10000; // 10 seconds
    const lastUpdate = orderUpdateCache.get(orderId);
    
    if (lastUpdate && (now - lastUpdate) < ORDER_UPDATE_COOLDOWN) {
      console.log(`Order ${orderId} recently updated, skipping API call`);
      return;
    }

    try {
      console.log('Updating order from database:', orderId);
      
      let updatedOrder: OrderOut;

      if (isAuthenticated && user) {
        // Authenticated users: use order ID endpoint
        try {
          updatedOrder = await apiService.getOrder(orderId);
        } catch (error: any) {
          if (error?.response?.status === 404) {
            // Order no longer exists server-side (e.g. an abandoned card
            // checkout that got cleaned up) - stop showing the stale
            // cached copy instead of leaving it around forever.
            console.log(`Order ${orderId} no longer exists - removing from local history`);
            setOrders(prevOrders => filterOrdersByOwnership(prevOrders.filter(o => o.id !== orderId)));
            return;
          }
          throw error;
        }

        // Verify ownership
        if (updatedOrder.customer_id !== user.id) {
          console.warn('Attempted to update order that does not belong to current user:', {
            orderId,
            orderCustomerId: updatedOrder.customer_id,
            currentUserId: user.id
          });
          // Remove the order if it doesn't belong to the current user
          setOrders(prevOrders => filterOrdersByOwnership(prevOrders.filter(o => o.id !== orderId)));
          return;
        }
      } else {
        // Anonymous users: find the order in current state to get QR code, then fetch by QR
        const currentOrder = orders.find(o => o.id === orderId);
        if (!currentOrder || (!currentOrder.qr_code_short && !currentOrder.qr_code)) {
          console.warn('Cannot update anonymous order: QR code not found for order', orderId);
          return;
        }
        
        const qrCode = currentOrder.qr_code_short || currentOrder.qr_code;
        
        // Check if this QR code is already being loaded to prevent duplicates
        if (loadingQrCodesRef.current.has(qrCode!)) {
          console.log(`QR code ${qrCode} is already being loaded for order ${orderId}, skipping duplicate request`);
          return;
        }
        
        // Mark QR code as loading
        loadingQrCodesRef.current.add(qrCode!);
        
        try {
          // Use skip_security=true for polling operations to avoid rate limiting
          updatedOrder = await apiService.getAnonymousOrderByQr(qrCode!, true);
        } finally {
          // Remove from loading set when done
          loadingQrCodesRef.current.delete(qrCode!);
        }
      }
      
      setOrders(prevOrders => {
        const existingOrderIndex = prevOrders.findIndex(o => o.id === orderId);
        if (existingOrderIndex >= 0) {
          // Update existing order
          const updatedOrders = [...prevOrders];
          updatedOrders[existingOrderIndex] = updatedOrder;
          console.log('Order updated from database:', updatedOrder);
          return isAuthenticated && user
            ? filterOrdersByOwnership(updatedOrders)
            : updatedOrders;
        } else {
          // Order not found in local storage, add it
          console.log('Order not in local storage, adding from database:', updatedOrder);
          return isAuthenticated && user
            ? filterOrdersByOwnership([updatedOrder, ...prevOrders])
            : [updatedOrder, ...prevOrders];
        }
      });
      
      // Update cache and last sync time
      setOrderUpdateCache(prev => new Map(prev.set(orderId, now)));
      setLastSyncTime(now);
    } catch (error) {
      console.error('Error updating order from database:', error);
      throw error;
    }
  };

  const syncOrdersFromDatabase = useCallback(async (force: boolean = false): Promise<void> => {
    // Prevent multiple simultaneous syncs
    if (isSyncing && !force) {
      console.log('Sync already in progress, skipping...');
      return;
    }

    // Check if we need to sync (avoid too frequent syncing)
    const now = Date.now();
    const SYNC_COOLDOWN = 30000; // 30 seconds cooldown between syncs
    
    if (!force && (now - lastSyncTime) < SYNC_COOLDOWN) {
      console.log('Sync cooldown active, skipping...');
      return;
    }

    try {
      setIsSyncing(true);
      console.log('Syncing all orders from database');
      
      if (isAuthenticated && user) {
        // Authenticated users: sync by order ID
        const filteredOrders = filterOrdersByOwnership(orders);
        const storedOrderIds = filteredOrders.map(order => order.id);
        
        if (storedOrderIds.length === 0) {
          console.log('No orders to sync');
          return;
        }

        // Fetch all orders from database in parallel. A 404 means the order
        // no longer exists server-side (e.g. an abandoned card checkout that
        // got cleaned up) - it must be dropped, not kept around stale. Any
        // other error is treated as transient: keep showing the cached copy.
        const orderResults = await Promise.all(
          storedOrderIds.map(orderId =>
            apiService.getOrder(orderId)
              .then(order => ({ orderId, order, deleted: false as const }))
              .catch(error => {
                if (error?.response?.status === 404) {
                  return { orderId, order: null, deleted: true as const };
                }
                console.warn(`Failed to fetch order ${orderId}:`, error);
                return { orderId, order: null, deleted: false as const };
              })
          )
        );

        const deletedOrderIds = new Set(
          orderResults.filter(r => r.deleted).map(r => r.orderId)
        );
        const validUpdatedOrders = orderResults
          .map(r => r.order)
          .filter((order): order is OrderOut => order !== null && order.customer_id === user.id);

        if (validUpdatedOrders.length > 0 || deletedOrderIds.size > 0) {
          console.log('Synced orders from database:', validUpdatedOrders, 'removed:', [...deletedOrderIds]);

          setOrders(prevOrders => {
            const ownedPrevOrders = filterOrdersByOwnership(prevOrders);
            const updatedOrdersMap = new Map(validUpdatedOrders.map(order => [order.id, order]));

            const updated = ownedPrevOrders
              .filter(order => !deletedOrderIds.has(order.id))
              .map(order => updatedOrdersMap.get(order.id) || order)
              .concat(
                validUpdatedOrders.filter(order => !ownedPrevOrders.some(prevOrder => prevOrder.id === order.id))
              );

            return filterOrdersByOwnership(updated);
          });
        } else {
          setOrders(prevOrders => filterOrdersByOwnership(prevOrders));
        }
      } else {
        // Anonymous users: sync by QR codes from localStorage
        const storedQrCodes = localStorage.getItem(ANONYMOUS_QR_CODES_KEY);
        if (!storedQrCodes) {
          console.log('No QR codes found for anonymous user');
          setOrders([]);
          return;
        }

        const qrCodes: string[] = JSON.parse(storedQrCodes);
        console.log('Syncing anonymous orders by QR codes:', qrCodes);

        // Filter out QR codes that are already being loaded to prevent duplicates
        const qrCodesToLoad = qrCodes.filter(qrCode => {
          if (loadingQrCodesRef.current.has(qrCode)) {
            console.log(`QR code ${qrCode} is already being loaded, skipping duplicate request`);
            return false;
          }
          loadingQrCodesRef.current.add(qrCode);
          return true;
        });

        // Fetch all orders using QR codes
        // Use skip_security=true for polling operations to avoid rate limiting
        const orderPromises = qrCodesToLoad.map(qrCode =>
          apiService.getAnonymousOrderByQr(qrCode, true)
            .catch(error => {
              // 400 means this QR no longer resolves to an order (e.g. an
              // abandoned card checkout that got cleaned up server-side) -
              // stop trying it again on every future sync/reload.
              if (error?.response?.status === 400) {
                try {
                  const currentStored = localStorage.getItem(ANONYMOUS_QR_CODES_KEY);
                  const currentCodes: string[] = currentStored ? JSON.parse(currentStored) : [];
                  const updatedQrCodes = currentCodes.filter(code => code !== qrCode);
                  if (updatedQrCodes.length > 0) {
                    localStorage.setItem(ANONYMOUS_QR_CODES_KEY, JSON.stringify(updatedQrCodes));
                  } else {
                    localStorage.removeItem(ANONYMOUS_QR_CODES_KEY);
                  }
                } catch (storageError) {
                  console.error('Error removing unusable QR code from localStorage:', storageError);
                }
              }
              console.warn(`Failed to fetch order with QR code ${qrCode}:`, error);
              return null;
            })
            .finally(() => {
              // Remove from loading set when done (success or error)
              loadingQrCodesRef.current.delete(qrCode);
            })
        );

        const fetchedOrders = await Promise.all(orderPromises);
        const validOrders = fetchedOrders.filter((order): order is OrderOut => order !== null);

        console.log('Synced anonymous orders by QR code:', validOrders);
        setOrders(validOrders);
      }

      // Update last sync time
      setLastSyncTime(now);
    } catch (error) {
      console.error('Error syncing orders from database:', error);
      throw error;
    } finally {
      setIsSyncing(false);
    }
  }, [orders, isAuthenticated, user, filterOrdersByOwnership]);

  const clearOrderHistory = () => {
    console.log('Clearing order history');
    setOrders([]);
    sessionStorage.removeItem(SESSION_STORAGE_KEY);
    // For anonymous users, also clear QR codes from localStorage
    if (!isAuthenticated) {
      localStorage.removeItem(ANONYMOUS_QR_CODES_KEY);
      console.log('Cleared anonymous QR codes from localStorage');
    }
  };

  const hasOrders = orders.length > 0;

  // Track previous auth state to detect changes
  const prevAuthStateRef = React.useRef<{ isAuthenticated: boolean; userId: number | null }>({
    isAuthenticated: false,
    userId: null
  });

  // Clear and re-filter orders when authentication status changes
  useEffect(() => {
    const currentAuthState = {
      isAuthenticated: !!isAuthenticated,
      userId: user?.id || null
    };

    // Only run if auth state actually changed
    const authChanged = 
      prevAuthStateRef.current.isAuthenticated !== currentAuthState.isAuthenticated ||
      prevAuthStateRef.current.userId !== currentAuthState.userId;

    if (!authChanged) {
      return; // No change, skip
    }

    // Update ref before making changes
    prevAuthStateRef.current = currentAuthState;

    // When auth status changes, clear/filter orders immediately
    if (isAuthenticated && user) {
      // User logged in: filter orders to only show theirs
      setOrders(prevOrders => {
        const filtered = filterOrdersByOwnership(prevOrders);
        console.log('User logged in - filtering orders:', {
          before: prevOrders.length,
          after: filtered.length,
          userId: user.id
        });
        // Update sessionStorage with filtered orders
        if (filtered.length > 0) {
          sessionStorage.setItem(SESSION_STORAGE_KEY, JSON.stringify(filtered));
        } else {
          sessionStorage.removeItem(SESSION_STORAGE_KEY);
        }
        return filtered;
      });

      // Re-sync orders when authentication status changes (with debouncing)
      const resyncOrders = async () => {
        try {
          await syncOrdersFromDatabase(true); // Force sync on auth change
        } catch (error) {
          console.error('Error re-syncing orders after auth change:', error);
        }
      };

      // Debounce the resync to avoid rapid calls
      const timeoutId = setTimeout(resyncOrders, 1000);
      return () => clearTimeout(timeoutId);
    } else {
      // User logged out or anonymous: Load orders by QR code if anonymous, clear if logged out
      if (prevAuthStateRef.current.isAuthenticated) {
        // User just logged out: clear everything
        console.log('User logged out - clearing all orders');
        setOrders([]);
        sessionStorage.removeItem(SESSION_STORAGE_KEY);
        sessionStorage.removeItem(ANONYMOUS_SESSION_KEY);
        // Don't clear QR codes - they might log back in as anonymous
      } else {
        // Already anonymous: load orders by QR code (handled in loadOrdersFromStorage)
        console.log('Anonymous user - orders will be loaded by QR code');
      }
    }
  }, [isAuthenticated, user, filterOrdersByOwnership, syncOrdersFromDatabase]);

  // Always return filtered orders (for authenticated users) or orders as-is (for anonymous users who created them)
  const filteredOrders = isAuthenticated && user 
    ? filterOrdersByOwnership(orders)
    : orders; // Anonymous users see orders they added during this session

  const value: OrderHistoryContextType = {
    orders: filteredOrders,
    addOrder,
    getOrder,
    updateOrderFromDatabase,
    syncOrdersFromDatabase,
    clearOrderHistory,
    hasOrders: filteredOrders.length > 0,
    isSyncing
  };

  return (
    <OrderHistoryContext.Provider value={value}>
      {children}
    </OrderHistoryContext.Provider>
  );
};

export const useOrderHistory = (): OrderHistoryContextType => {
  const context = useContext(OrderHistoryContext);
  if (!context) {
    throw new Error('useOrderHistory must be used within an OrderHistoryProvider');
  }
  return context;
};